import React, { useEffect, useMemo, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import { useLoading } from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";

import BodyWithBreadcrumb from "components/molecules/BodyWithBreadcrumb";
import Breadcrumb from "components/molecules/Breadcrumb";
import PlanningStepSection from "components/page/searchAgent/PlanningStepSection";
import RequestStepSection from "components/page/searchAgent/RequestStepSection";
import ResultStepSection from "components/page/searchAgent/ResultStepSection";
import {
    INITIAL_REQUIREMENT_FIELDS,
    SOURCE_LABEL_MAP,
    STEP_ORDER,
    buildDefaultInitialSelections,
    resolveInitialRequirements,
} from "components/page/searchAgent/constants";
import LoginUserLayout from "components/templates/LoginUserLayout";

import { useSearchAgentWebSocket } from "hooks/useSearchAgentWebSocket";

const PageWrap = styled.div`
    width: 100%;
    max-width: 1000px;
    margin: 20px auto 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const StepperWrap = styled.div`
    display: flex;
    gap: 8px;
`;

const StepItem = styled.div`
    flex: 1;
    border: 1px solid ${(props) => (props.active ? "var(--color-Key)" : "var(--color-Outline)")};
    border-radius: 8px;
    background: ${(props) => (props.active ? "var(--color-Base1)" : "var(--color-White)")};
    padding: 10px 12px;
`;

function SearchAgentPage() {
    const navigate = useNavigate();
    const match = useMatch("/:space_uuid/*");
    const spaceUuid = match?.params?.space_uuid ?? "";
    const [queryInput, setQueryInput] = useState("");
    const [initialSelections, setInitialSelections] = useState(buildDefaultInitialSelections);
    const [initialCustomInputs, setInitialCustomInputs] = useState({});
    const [editablePlanText, setEditablePlanText] = useState("");
    const { setLoading } = useLoading();

    const {
        ready,
        step,
        phase,
        error,
        planningState,
        resultState,
        submitRequest,
        confirmPlanning,
        saveResearch,
        goBackStep,
        cancelSession,
    } = useSearchAgentWebSocket({ connectionDependencies: [true] });

    const hasInvalidInitialRequirementCustomInput = useMemo(() => {
        return INITIAL_REQUIREMENT_FIELDS.some((field) => {
            if (initialSelections[field.category] !== "other") return false;
            return !(initialCustomInputs[field.category] || "").trim();
        });
    }, [initialSelections, initialCustomInputs]);

    const canSubmitRequest = ready && queryInput.trim() && !hasInvalidInitialRequirementCustomInput;

    const activeStepIndex = Math.max(STEP_ORDER.indexOf(step), 0);

    useEffect(() => {
        setEditablePlanText(planningState.planText || "");
    }, [planningState.planText]);

    const latestLogMessage = useMemo(() => {
        const latestLog = resultState.logs[resultState.logs.length - 1];
        return (latestLog?.message || "").trim() || "관련 논문을 수집하고 리포트를 작성하고 있습니다.";
    }, [resultState.logs]);

    const hasReportContent = useMemo(() => !!(resultState.report || "").trim(), [resultState.report]);

    useEffect(() => {
        if (step === "result" && resultState.streaming && !hasReportContent) {
            setLoading(true, "리서치를 진행 중입니다.", latestLogMessage);
            return;
        }
        setLoading(false);
    }, [hasReportContent, latestLogMessage, resultState.streaming, setLoading, step]);

    useEffect(() => {
        return () => {
            setLoading(false);
        };
    }, [setLoading]);

    const handleSubmitRequest = () => {
        const initialRequirements = resolveInitialRequirements(initialSelections, initialCustomInputs);
        if (!initialRequirements) return;
        submitRequest(queryInput, initialRequirements);
        setEditablePlanText("");
    };

    const handleConfirmPlanning = () => {
        const planText = editablePlanText.trim();
        if (!planText) return;
        confirmPlanning(planText);
    };

    const handleOpenResearch = () => {
        if (!spaceUuid || !resultState.researchNo) return;
        navigate(`/${spaceUuid}/research/${resultState.researchNo}`);
    };

    const handleSaveResearch = () => {
        if (resultState.isSaving || resultState.isPersisted) return;
        saveResearch();
    };

    return (
        <LoginUserLayout>
            <HtmlHead title={"Search"} />
            <Breadcrumb>
                <Text variant="styled-lg" fontWeight="500" color="var(--color-Black)">
                    Search
                </Text>
            </Breadcrumb>

            <BodyWithBreadcrumb>
                <PageWrap>
                    <StepperWrap>
                        <StepItem active={activeStepIndex >= 0 && step === "request"}>
                            <Text variant="styled-md" fontWeight="500" color="var(--color-SubBlack)">
                                1. 요청
                            </Text>
                            <Text variant="styled-sm" color="var(--color-Grey1)">
                                검색할 주제와 기본 조건 입력
                            </Text>
                        </StepItem>
                        <StepItem active={activeStepIndex >= 1 && step === "planning"}>
                            <Text variant="styled-md" fontWeight="500" color="var(--color-SubBlack)">
                                2. 계획
                            </Text>
                            <Text variant="styled-sm" color="var(--color-Grey1)">
                                검색 계획 확인 및 수정
                            </Text>
                        </StepItem>
                        <StepItem active={activeStepIndex >= 2 && step === "result"}>
                            <Text variant="styled-md" fontWeight="500" color="var(--color-SubBlack)">
                                3. 결과
                            </Text>
                            <Text variant="styled-sm" color="var(--color-Grey1)">
                                검색 결과 생성 및 검토
                            </Text>
                        </StepItem>
                    </StepperWrap>

                    {step === "request" && (
                        <RequestStepSection
                            ready={ready}
                            queryInput={queryInput}
                            onQueryInputChange={setQueryInput}
                            initialFields={INITIAL_REQUIREMENT_FIELDS}
                            initialSelections={initialSelections}
                            initialCustomInputs={initialCustomInputs}
                            onSelectInitialOption={(category, optionId) =>
                                setInitialSelections((prev) => ({
                                    ...prev,
                                    [category]: optionId,
                                }))
                            }
                            onChangeInitialCustomInput={(category, value) =>
                                setInitialCustomInputs((prev) => ({
                                    ...prev,
                                    [category]: value,
                                }))
                            }
                            onSubmitRequest={handleSubmitRequest}
                            canSubmitRequest={canSubmitRequest}
                        />
                    )}

                    {step === "planning" && (
                        <PlanningStepSection
                            planningState={planningState}
                            editablePlanText={editablePlanText}
                            onEditablePlanTextChange={setEditablePlanText}
                            onConfirmPlanning={handleConfirmPlanning}
                            onGoBackRequest={() => goBackStep("request")}
                        />
                    )}

                    {step === "result" && (
                        <ResultStepSection
                            phase={phase}
                            error={error}
                            resultState={resultState}
                            sourceLabelMap={SOURCE_LABEL_MAP}
                            onGoBackPlanning={() => goBackStep("planning")}
                            onCancelSession={cancelSession}
                            onOpenResearch={handleOpenResearch}
                            onSaveResearch={handleSaveResearch}
                        />
                    )}
                </PageWrap>
            </BodyWithBreadcrumb>
        </LoginUserLayout>
    );
}

export default SearchAgentPage;
